import {useCallback, useEffect, useMemo, useState} from 'react';
import {
    getLegacyImportJob,
    previewLegacySection,
    previewLegacySectionByYear,
    previewLegacySectionYears,
    previewLegacySummary,
    startLegacyImportJob,
} from '../../../api';
import {
    GROUP_BY_YEAR_KEYS,
    SECTION_IMPORT_ORDER,
    SECTION_KEY_GETTERS,
    SECTION_PAGE_SIZE,
    SECTION_PAYLOAD_KEYS,
    SINGLE_SELECT_KEYS,
} from './accountImportConstants';
import {
    buildYearId,
    createDefaultSelection,
    createEmptySelectionPayload,
    normalizeYearKey,
    resolveItemYearKey,
} from './accountImportUtils';

const resolveSectionCounts = (summary) => {
    if (!summary) {
        return null;
    }
    return {
        profiles: summary.profiles || 0,
        customers: summary.customers || 0,
        suppliers: summary.suppliers || 0,
        articles: summary.articles || 0,
        shippingCosts: summary.shippingCosts || 0,
        invoices: summary.invoices || 0,
        bookings: summary.bookings || 0,
    };
};

export default function useAccountImport() {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewSummary, setPreviewSummary] = useState(null);
    const [selection, setSelection] = useState(null);
    const [sectionData, setSectionData] = useState({});
    const [sectionYears, setSectionYears] = useState({});
    const [yearData, setYearData] = useState({});
    const [yearLoading, setYearLoading] = useState({});
    const [yearErrors, setYearErrors] = useState({});
    const [expandedYears, setExpandedYears] = useState({});
    const [yearSelection, setYearSelection] = useState({});
    const [sectionLoading, setSectionLoading] = useState({});
    const [sectionErrors, setSectionErrors] = useState({});
    const [previewWarnings, setPreviewWarnings] = useState([]);
    const [importWarnings, setImportWarnings] = useState([]);
    const [importResult, setImportResult] = useState(null);
    const [importError, setImportError] = useState('');
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [loadingImport, setLoadingImport] = useState(false);
    const [importProgress, setImportProgress] = useState(null);
    const [importJobId, setImportJobId] = useState(null);

    const resetState = useCallback(() => {
        setPreviewSummary(null);
        setSelection(null);
        setSectionData({});
        setSectionYears({});
        setYearData({});
        setYearLoading({});
        setYearErrors({});
        setExpandedYears({});
        setYearSelection({});
        setSectionLoading({});
        setSectionErrors({});
        setPreviewWarnings([]);
        setImportWarnings([]);
        setImportResult(null);
        setImportError('');
        setImportProgress(null);
        setImportJobId(null);
    }, []);

    const handleFileSelect = useCallback((file) => {
        if (!file) {
            setSelectedFile(null);
            return;
        }
        setSelectedFile(file);
        resetState();
    }, [resetState]);

    const handlePreview = useCallback(async () => {
        if (!selectedFile) {
            setImportError('Bitte eine Datei auswählen.');
            return;
        }
        const formData = new FormData();
        formData.append('file', selectedFile);
        setLoadingPreview(true);
        setImportError('');
        setPreviewWarnings([]);
        setImportWarnings([]);
        setImportResult(null);
        setPreviewSummary(null);
        setSelection(null);
        setSectionData({});
        setSectionYears({});
        setYearData({});
        setYearLoading({});
        setYearErrors({});
        setExpandedYears({});
        setYearSelection({});
        setSectionLoading({});
        setSectionErrors({});
        try {
            const res = await previewLegacySummary(formData);
            if (res.status >= 200 && res.status < 300) {
                const payload = res.data && typeof res.data === 'object' ? res.data : null;
                setPreviewSummary(payload);
                setPreviewWarnings(Array.isArray(payload?.warnings) ? payload.warnings : []);
                setSelection(createDefaultSelection());
            } else {
                const message = typeof res.data === 'string' && res.data.trim().length > 0
                    ? res.data
                    : 'Vorschau fehlgeschlagen.';
                setImportError(message);
            }
        } catch (error) {
            const backendMessage = error?.response?.data;
            const message = typeof backendMessage === 'string' && backendMessage.trim().length > 0
                ? backendMessage
                : 'Vorschau fehlgeschlagen.';
            setImportError(message);
        } finally {
            setLoadingPreview(false);
        }
    }, [selectedFile]);

    const sectionCounts = useMemo(() => resolveSectionCounts(previewSummary), [previewSummary]);

    const getSectionSelectionMetrics = useCallback((key, count) => {
        const entry = selection?.[key];
        const total = count || 0;
        if (!entry || total <= 0) {
            return { allSelected: false, someSelected: false, selectedCount: 0 };
        }
        if (entry.mode === 'all') {
            const excluded = entry.values.size;
            const selectedCount = Math.max(total - excluded, 0);
            return {
                allSelected: excluded === 0,
                someSelected: excluded > 0 && excluded < total,
                selectedCount,
            };
        }
        const included = entry.values.size;
        return {
            allSelected: included >= total,
            someSelected: included > 0 && included < total,
            selectedCount: Math.min(included, total),
        };
    }, [selection]);

    const isItemSelected = useCallback((key, value, yearKey = null) => {
        const normalizedYear = yearKey ? normalizeYearKey(yearKey) : null;
        const yearEntry = normalizedYear ? yearSelection?.[key]?.[normalizedYear] : null;
        const entry = yearEntry || selection?.[key];
        if (!entry) {
            return false;
        }
        if (entry.mode === 'all') {
            return !entry.values.has(value);
        }
        return entry.values.has(value);
    }, [selection, yearSelection]);

    const getYearSelectionMetrics = useCallback((key, yearKey, items, getKey, totalCount = 0) => {
        const normalizedYear = normalizeYearKey(yearKey);
        const yearEntry = yearSelection?.[key]?.[normalizedYear];
        if (yearEntry) {
            const selectedCount = yearEntry.mode === 'all'
                ? Math.max(totalCount - yearEntry.values.size, 0)
                : Math.min(yearEntry.values.size, totalCount);
            return {
                allSelected: totalCount > 0 && selectedCount === totalCount,
                someSelected: selectedCount > 0 && selectedCount < totalCount,
                selectedCount,
            };
        }
        if (items && items.length) {
            const selectedCount = items.reduce((sum, item) => {
                const value = getKey(item);
                if (value === null || value === undefined || value === '') {
                    return sum;
                }
                return isItemSelected(key, value, normalizedYear) ? sum + 1 : sum;
            }, 0);
            const total = items.length;
            return {
                allSelected: selectedCount === total,
                someSelected: selectedCount > 0 && selectedCount < total,
                selectedCount,
            };
        }
        const entry = selection?.[key];
        if (!entry) {
            return { allSelected: false, someSelected: false, selectedCount: 0 };
        }
        if (entry.mode === 'all' && totalCount > 0) {
            return { allSelected: true, someSelected: false, selectedCount: totalCount };
        }
        return { allSelected: false, someSelected: false, selectedCount: 0 };
    }, [isItemSelected, selection, yearSelection]);

    const getSectionYearSelectionMetrics = useCallback((key, totalCount) => {
        const years = sectionYears?.[key];
        if (!Array.isArray(years) || !years.length) {
            return getSectionSelectionMetrics(key, totalCount);
        }
        const selectedCount = years.reduce((sum, entry) => {
            const yearKey = normalizeYearKey(entry?.year);
            const yearCount = entry?.count || 0;
            const items = yearData?.[key]?.[yearKey];
            const itemsWithKey = Array.isArray(items)
                ? items.filter((item) => {
                    const value = SECTION_KEY_GETTERS[key](item);
                    return value !== null && value !== undefined && value !== '';
                })
                : null;
            const metrics = getYearSelectionMetrics(
                key,
                yearKey,
                itemsWithKey,
                SECTION_KEY_GETTERS[key],
                yearCount
            );
            return sum + metrics.selectedCount;
        }, 0);
        return {
            allSelected: totalCount > 0 && selectedCount === totalCount,
            someSelected: selectedCount > 0 && selectedCount < totalCount,
            selectedCount,
        };
    }, [getSectionSelectionMetrics, getYearSelectionMetrics, sectionYears, yearData]);

    const totalSelected = useMemo(() => {
        if (!selection || !sectionCounts) {
            return 0;
        }
        return Object.keys(sectionCounts).reduce((sum, key) => {
            const count = sectionCounts[key] || 0;
            if (!count) {
                return sum;
            }
            if (GROUP_BY_YEAR_KEYS.has(key) && Array.isArray(sectionYears[key])) {
                const metrics = getSectionYearSelectionMetrics(key, count);
                return sum + metrics.selectedCount;
            }
            const entry = selection[key];
            if (!entry) {
                return sum;
            }
            if (entry.mode === 'all') {
                return sum + Math.max(count - entry.values.size, 0);
            }
            return sum + Math.min(entry.values.size, count);
        }, 0);
    }, [selection, sectionCounts, sectionYears, getSectionYearSelectionMetrics]);

    const fetchSectionPage = useCallback(async (key, offset) => {
        const formData = new FormData();
        formData.append('file', selectedFile);
        const res = await previewLegacySection(formData, key, offset, SECTION_PAGE_SIZE);
        if (res.status >= 200 && res.status < 300) {
            return Array.isArray(res.data) ? res.data : [];
        }
        const message = typeof res.data === 'string' && res.data.trim().length > 0
            ? res.data
            : 'Vorschau fehlgeschlagen.';
        throw new Error(message);
    }, [selectedFile]);

    const getYearCount = useCallback((key, yearKey) => {
        const years = sectionYears?.[key];
        if (!Array.isArray(years)) {
            return 0;
        }
        const match = years.find((entry) => {
            return normalizeYearKey(entry?.year) === normalizeYearKey(yearKey);
        });
        return match?.count || 0;
    }, [sectionYears]);

    const loadSectionYears = useCallback(async (key) => {
        if (!selectedFile || !previewSummary) {
            return;
        }
        if (sectionLoading[key] || Array.isArray(sectionYears[key])) {
            return;
        }
        const formData = new FormData();
        formData.append('file', selectedFile);
        setSectionLoading((prev) => ({ ...prev, [key]: true }));
        setSectionErrors((prev) => ({ ...prev, [key]: '' }));
        try {
            const res = await previewLegacySectionYears(formData, key);
            if (res.status >= 200 && res.status < 300) {
                const items = Array.isArray(res.data) ? res.data : [];
                setSectionYears((prev) => ({ ...prev, [key]: items }));
            } else {
                const message = typeof res.data === 'string' && res.data.trim().length > 0
                    ? res.data
                    : 'Vorschau fehlgeschlagen.';
                setSectionErrors((prev) => ({ ...prev, [key]: message }));
            }
        } catch (error) {
            const backendMessage = error?.response?.data;
            const message = typeof backendMessage === 'string' && backendMessage.trim().length > 0
                ? backendMessage
                : 'Vorschau fehlgeschlagen.';
            setSectionErrors((prev) => ({ ...prev, [key]: message }));
        } finally {
            setSectionLoading((prev) => ({ ...prev, [key]: false }));
        }
    }, [previewSummary, sectionLoading, sectionYears, selectedFile]);

    const loadYearItems = useCallback(async (key, yearKey, { loadMore = false, loadAll = false } = {}) => {
        if (!selectedFile || !previewSummary) {
            return;
        }
        const yearId = buildYearId(key, yearKey);
        const existingItems = yearData?.[key]?.[yearKey] || [];
        const totalCount = getYearCount(key, yearKey);
        if (yearLoading[yearId]) {
            return;
        }
        if (loadAll && existingItems.length >= totalCount) {
            return;
        }
        if (!loadAll) {
            if (loadMore && existingItems.length >= totalCount) {
                return;
            }
            if (!loadMore && existingItems.length) {
                return;
            }
        }
        setYearLoading((prev) => ({ ...prev, [yearId]: true }));
        setYearErrors((prev) => ({ ...prev, [yearId]: '' }));
        try {
            let collected = [...existingItems];
            if (loadAll) {
                for (let offset = collected.length; offset < totalCount; offset += SECTION_PAGE_SIZE) {
                    const formData = new FormData();
                    formData.append('file', selectedFile);
                    const res = await previewLegacySectionByYear(formData, key, yearKey, offset, SECTION_PAGE_SIZE);
                    if (res.status >= 200 && res.status < 300) {
                        const items = Array.isArray(res.data) ? res.data : [];
                        collected = [...collected, ...items];
                    } else {
                        const message = typeof res.data === 'string' && res.data.trim().length > 0
                            ? res.data
                            : 'Vorschau fehlgeschlagen.';
                        throw new Error(message);
                    }
                }
            } else {
                const formData = new FormData();
                formData.append('file', selectedFile);
                const offset = loadMore ? existingItems.length : 0;
                const res = await previewLegacySectionByYear(formData, key, yearKey, offset, SECTION_PAGE_SIZE);
                if (res.status >= 200 && res.status < 300) {
                    const items = Array.isArray(res.data) ? res.data : [];
                    collected = loadMore ? [...existingItems, ...items] : items;
                } else {
                    const message = typeof res.data === 'string' && res.data.trim().length > 0
                        ? res.data
                        : 'Vorschau fehlgeschlagen.';
                    throw new Error(message);
                }
            }

            setYearData((prev) => {
                const current = prev[key] || {};
                return { ...prev, [key]: { ...current, [yearKey]: collected } };
            });
        } catch (error) {
            const backendMessage = error?.response?.data;
            const message = typeof backendMessage === 'string' && backendMessage.trim().length > 0
                ? backendMessage
                : error?.message || 'Vorschau fehlgeschlagen.';
            setYearErrors((prev) => ({ ...prev, [yearId]: message }));
        } finally {
            setYearLoading((prev) => ({ ...prev, [yearId]: false }));
        }
    }, [getYearCount, previewSummary, selectedFile, yearData, yearLoading]);

    const handleImport = useCallback(async () => {
        if (!selectedFile) {
            setImportError('Bitte eine Datei auswählen.');
            return;
        }
        if (!previewSummary || !selection) {
            setImportError('Bitte zuerst die Vorschau laden.');
            return;
        }
        if (totalSelected === 0) {
            setImportError('Keine Daten fuer den Import ausgewählt.');
            return;
        }
        setLoadingImport(true);
        setImportError('');
        setImportWarnings([]);
        setImportResult(null);
        try {
            const payload = createEmptySelectionPayload();
            const totalPages = SECTION_IMPORT_ORDER.reduce((sum, sectionKey) => {
                const totalCount = sectionCounts?.[sectionKey] || 0;
                return sum + Math.ceil(totalCount / SECTION_PAGE_SIZE);
            }, 0);
            setImportProgress({
                total: totalPages,
                completed: 0,
                phase: 'Importauswahl wird vorbereitet',
            });

            for (const sectionKey of SECTION_IMPORT_ORDER) {
                const totalCount = sectionCounts?.[sectionKey] || 0;
                if (!totalCount) {
                    continue;
                }
                const sectionTotalPages = Math.ceil(totalCount / SECTION_PAGE_SIZE);
                for (let offset = 0; offset < totalCount; offset += SECTION_PAGE_SIZE) {
                    const sectionPage = Math.floor(offset / SECTION_PAGE_SIZE) + 1;
                    setImportProgress((prev) => ({
                        ...prev,
                        sectionKey,
                        sectionPage,
                        sectionTotalPages,
                    }));
                    const items = await fetchSectionPage(sectionKey, offset);
                    if (items.length) {
                        const keyExtractor = SECTION_KEY_GETTERS[sectionKey];
                        const selectedIds = items.reduce((acc, item) => {
                            const value = keyExtractor(item);
                            if (value === null || value === undefined || value === '') {
                                return acc;
                            }
                            const yearKey = resolveItemYearKey(sectionKey, item);
                            if (isItemSelected(sectionKey, value, yearKey)) {
                                acc.push(value);
                            }
                            return acc;
                        }, []);
                        if (selectedIds.length) {
                            payload[SECTION_PAYLOAD_KEYS[sectionKey]].push(...selectedIds);
                        }
                    }
                    setImportProgress((prev) => {
                        if (!prev) {
                            return prev;
                        }
                        const total = prev.total || 0;
                        const nextCompleted = prev.completed + 1;
                        return {
                            ...prev,
                            completed: total > 0 ? Math.min(nextCompleted, total) : nextCompleted,
                        };
                    });
                }
            }

            setImportProgress({
                total: 1,
                completed: 0,
                phase: 'Import-Job wird gestartet',
            });
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('selection', JSON.stringify(payload));
            const res = await startLegacyImportJob(formData);
            if (res.status >= 200 && res.status < 300) {
                const data = res.data && typeof res.data === 'object' ? res.data : null;
                if (!data?.jobId) {
                    throw new Error('Import-Job wurde ohne Job-ID gestartet.');
                }
                setImportJobId(data.jobId);
            } else {
                const message = typeof res.data === 'string' && res.data.trim().length > 0
                    ? res.data
                    : 'Import fehlgeschlagen.';
                throw new Error(message);
            }
        } catch (error) {
            const backendMessage = error?.response?.data;
            const message = typeof backendMessage === 'string' && backendMessage.trim().length > 0
                ? backendMessage
                : error?.message || 'Import fehlgeschlagen.';
            setImportError(message);
            setLoadingImport(false);
            setImportProgress(null);
        }
    }, [fetchSectionPage, isItemSelected, previewSummary, sectionCounts, selectedFile, selection, totalSelected]);

    useEffect(() => {
        if (!importJobId) {
            return undefined;
        }

        let active = true;
        const pollJob = async () => {
            try {
                const res = await getLegacyImportJob(importJobId);
                if (!active) {
                    return;
                }
                if (res.status >= 200 && res.status < 300) {
                    const data = res.data && typeof res.data === 'object' ? res.data : null;
                    if (!data) {
                        throw new Error('Import-Status konnte nicht gelesen werden.');
                    }
                    setImportProgress({
                        total: data.totalSteps || 0,
                        completed: data.completedSteps || 0,
                        phase: data.phase || 'Import wird verarbeitet',
                        status: data.status || '',
                    });
                    if (data.status === 'COMPLETED') {
                        setImportResult(data.result || null);
                        setImportWarnings(Array.isArray(data.result?.warnings) ? data.result.warnings : []);
                        setLoadingImport(false);
                        setImportProgress(null);
                        setImportJobId(null);
                        return;
                    }
                    if (data.status === 'FAILED') {
                        setImportError(data.errorMessage || 'Import fehlgeschlagen.');
                        setLoadingImport(false);
                        setImportProgress(null);
                        setImportJobId(null);
                        return;
                    }
                } else {
                    const message = typeof res.data === 'string' && res.data.trim().length > 0
                        ? res.data
                        : 'Import-Status konnte nicht gelesen werden.';
                    throw new Error(message);
                }
            } catch (error) {
                if (!active) {
                    return;
                }
                const backendMessage = error?.response?.data;
                const message = typeof backendMessage === 'string' && backendMessage.trim().length > 0
                    ? backendMessage
                    : error?.message || 'Import-Status konnte nicht gelesen werden.';
                setImportError(message);
                setLoadingImport(false);
                setImportProgress(null);
                setImportJobId(null);
                return;
            }
            if (active) {
                window.setTimeout(pollJob, 1000);
            }
        };

        pollJob();
        return () => {
            active = false;
        };
    }, [importJobId]);

    const ensureSingleSelection = useCallback((key, items) => {
        if (!SINGLE_SELECT_KEYS.has(key)) {
            return;
        }
        if (!Array.isArray(items) || !items.length) {
            return;
        }
        const getKey = SECTION_KEY_GETTERS[key];
        if (!getKey) {
            return;
        }
        const firstItem = items.find((item) => {
            const value = getKey(item);
            return value !== null && value !== undefined && value !== '';
        });
        if (!firstItem) {
            return;
        }
        const firstValue = getKey(firstItem);
        setSelection((prev) => {
            const base = prev || createDefaultSelection();
            const entry = base[key];
            const values = entry?.values || new Set();
            if (entry?.mode === 'include' && values.size > 0) {
                return base;
            }
            return { ...base, [key]: { mode: 'include', values: new Set([firstValue]) } };
        });
    }, []);

    const toggleSelection = useCallback((key, value, yearKey = null) => {
        if (!selection) {
            return;
        }
        const normalizedYear = yearKey ? normalizeYearKey(yearKey) : null;
        const yearEntry = normalizedYear ? yearSelection?.[key]?.[normalizedYear] : null;
        if (yearEntry) {
            setYearSelection((prev) => {
                const sectionYears = prev[key] || {};
                const entry = sectionYears[normalizedYear] || { mode: yearEntry.mode, values: new Set() };
                const nextValues = new Set(entry.values);
                if (entry.mode === 'all') {
                    if (nextValues.has(value)) {
                        nextValues.delete(value);
                    } else {
                        nextValues.add(value);
                    }
                    return { ...prev, [key]: { ...sectionYears, [normalizedYear]: { mode: 'all', values: nextValues } } };
                }
                if (nextValues.has(value)) {
                    nextValues.delete(value);
                } else {
                    nextValues.add(value);
                }
                return { ...prev, [key]: { ...sectionYears, [normalizedYear]: { mode: 'include', values: nextValues } } };
            });
            return;
        }

        if (SINGLE_SELECT_KEYS.has(key)) {
            setSelection((prev) => {
                const entry = prev[key] || { mode: 'include', values: new Set() };
                const isSelected = entry.values.has(value);
                const nextValues = isSelected ? new Set() : new Set([value]);
                return { ...prev, [key]: { mode: 'include', values: nextValues } };
            });
            return;
        }

        setSelection((prev) => {
            const entry = prev[key] || { mode: 'all', values: new Set() };
            const nextValues = new Set(entry.values);
            if (entry.mode === 'all') {
                if (nextValues.has(value)) {
                    nextValues.delete(value);
                } else {
                    nextValues.add(value);
                }
                return { ...prev, [key]: { mode: 'all', values: nextValues } };
            }
            if (nextValues.has(value)) {
                nextValues.delete(value);
            } else {
                nextValues.add(value);
            }
            return { ...prev, [key]: { mode: 'include', values: nextValues } };
        });
    }, [selection, yearSelection]);

    const toggleAll = useCallback((key, allSelected) => {
        if (!selection) {
            return;
        }
        if (SINGLE_SELECT_KEYS.has(key)) {
            return;
        }
        setSelection((prev) => {
            return {
                ...prev,
                [key]: allSelected
                    ? { mode: 'include', values: new Set() }
                    : { mode: 'all', values: new Set() },
            };
        });
    }, [selection]);

    const setYearExpanded = useCallback((key, yearKey, expanded) => {
        setExpandedYears((prev) => {
            const current = prev[key] || {};
            return { ...prev, [key]: { ...current, [yearKey]: expanded } };
        });
    }, []);

    const setYearSelectionMode = useCallback((key, yearKey, mode) => {
        if (!selection) {
            return;
        }
        const normalizedYear = normalizeYearKey(yearKey);
        const globalMode = selection[key]?.mode || 'all';
        setYearSelection((prev) => {
            const sectionYears = prev[key] || {};
            if (mode === globalMode) {
                const nextSectionYears = { ...sectionYears };
                delete nextSectionYears[normalizedYear];
                if (!Object.keys(nextSectionYears).length) {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                }
                return { ...prev, [key]: nextSectionYears };
            }
            return {
                ...prev,
                [key]: {
                    ...sectionYears,
                    [normalizedYear]: { mode, values: new Set() },
                },
            };
        });
    }, [selection]);

    const handleYearToggle = useCallback((key, yearKey, totalCount, allSelected) => {
        if (totalCount <= 0) {
            return;
        }
        const nextMode = allSelected ? 'include' : 'all';
        setYearSelectionMode(key, yearKey, nextMode);
    }, [setYearSelectionMode]);

    const loadSection = useCallback(async (key, { loadMore = false, loadAll = false } = {}) => {
        if (!selectedFile || !previewSummary) {
            return;
        }
        const existingItems = Array.isArray(sectionData[key]) ? sectionData[key] : [];
        const offset = loadMore ? existingItems.length : 0;
        const totalCount = sectionCounts?.[key] || 0;
        if (sectionLoading[key]) {
            return;
        }
        if (loadAll && existingItems.length >= totalCount) {
            return;
        }
        if (!loadAll) {
            if (loadMore && existingItems.length >= totalCount) {
                return;
            }
            if (!loadMore && existingItems.length) {
                return;
            }
        }
        setSectionLoading((prev) => ({ ...prev, [key]: true }));
        setSectionErrors((prev) => ({ ...prev, [key]: '' }));
        try {
            if (loadAll) {
                let collected = [...existingItems];
                for (let pageOffset = collected.length; pageOffset < totalCount; pageOffset += SECTION_PAGE_SIZE) {
                    const items = await fetchSectionPage(key, pageOffset);
                    collected = [...collected, ...items];
                }
                setSectionData((prev) => ({ ...prev, [key]: collected }));
                ensureSingleSelection(key, collected);
            } else {
                const formData = new FormData();
                formData.append('file', selectedFile);
                const res = await previewLegacySection(formData, key, offset, SECTION_PAGE_SIZE);
                if (res.status >= 200 && res.status < 300) {
                    const items = Array.isArray(res.data) ? res.data : [];
                    setSectionData((prev) => {
                        const current = Array.isArray(prev[key]) ? prev[key] : [];
                        return {
                            ...prev,
                            [key]: loadMore ? [...current, ...items] : items,
                        };
                    });
                    ensureSingleSelection(key, items);
                } else {
                    const message = typeof res.data === 'string' && res.data.trim().length > 0
                        ? res.data
                        : 'Vorschau fehlgeschlagen.';
                    setSectionErrors((prev) => ({ ...prev, [key]: message }));
                }
            }
        } catch (error) {
            const backendMessage = error?.response?.data;
            const message = typeof backendMessage === 'string' && backendMessage.trim().length > 0
                ? backendMessage
                : 'Vorschau fehlgeschlagen.';
            setSectionErrors((prev) => ({ ...prev, [key]: message }));
        } finally {
            setSectionLoading((prev) => ({ ...prev, [key]: false }));
        }
    }, [ensureSingleSelection, fetchSectionPage, previewSummary, sectionCounts, sectionData, sectionLoading, selectedFile]);

    useEffect(() => {
        if (!previewSummary || !selectedFile) {
            return;
        }
        const profileCount = previewSummary.profiles || 0;
        if (!profileCount) {
            return;
        }
        if (Array.isArray(sectionData.profiles) || sectionLoading.profiles) {
            return;
        }
        loadSection('profiles');
    }, [previewSummary, selectedFile, sectionData.profiles, sectionLoading.profiles, loadSection]);

    return {
        selectedFile,
        previewSummary,
        selection,
        sectionData,
        sectionYears,
        yearData,
        yearLoading,
        yearErrors,
        expandedYears,
        yearSelection,
        sectionLoading,
        sectionErrors,
        previewWarnings,
        importWarnings,
        importResult,
        importError,
        loadingPreview,
        loadingImport,
        importProgress,
        resetState,
        handleFileSelect,
        handlePreview,
        handleImport,
        sectionCounts,
        totalSelected,
        getSectionSelectionMetrics,
        getSectionYearSelectionMetrics,
        getYearSelectionMetrics,
        getYearCount,
        isItemSelected,
        loadSection,
        loadSectionYears,
        loadYearItems,
        toggleSelection,
        toggleAll,
        setYearExpanded,
        handleYearToggle,
        setExpandedYears,
    };
}
